import { get, toNumber } from 'lodash'
import { Injectable } from '@angular/core';
import { DeviceSettingsFeatureToggle } from 'src/app/enums/feature-toggle.enum';
import { IDeviceSettingsDefaultValueService } from '../../device-settings-default-value.interface';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ADPolicyState } from 'src/app/constants/ad-policy.constants';
import { PowerService } from 'src/app/services/power/power.service';
import { IPowerBatterySettingsDefaultValueModel } from 'src/app/data-models/feature-toggle/power/power-battery-settings-default-value.interface';
import { ChargeThreshold } from 'src/app/data-models/device/charge-threshold.model';

@Injectable({
	providedIn: 'root'
})
export class PowerBatterySettingsDefaultValueService implements IDeviceSettingsDefaultValueService {

	private readonly defaultStartValue = 40;
	private readonly defaultStopValue = 80;

	private defaultValues: IPowerBatterySettingsDefaultValueModel;

	constructor(
		private powerService: PowerService,
		private logger: LoggerService) { }

	initialize(adTags: any): void {
		const powerBatterySettings: IPowerBatterySettingsDefaultValueModel | Boolean = get(adTags, DeviceSettingsFeatureToggle.PowerBatterySettings);
		this.logger.info('PowerBatterySettingsDefaultValueService - Initialize', { powerBatterySettings });

		if (powerBatterySettings !== ADPolicyState.DISABLED && powerBatterySettings !== ADPolicyState.NOT_CONFIGURED) {
			this.defaultValues = powerBatterySettings as IPowerBatterySettingsDefaultValueModel;
			this.setPowerBatterySettings();
		}
	}

	private setPowerBatterySettings() {
		const conservationModeDefaultValue = false;
		const rapidChargeDefaultValue = false;

		this.setBatteryThresholdDefaultValue();
		this.setConservationMode(conservationModeDefaultValue);
		this.setRapidCharge(rapidChargeDefaultValue);
	}

	private async setConservationMode(defaultValue: boolean) {
		this.logger.info('PowerBatterySettingsDefaultValueService - setConservationMode - before');
		try {
			const conservationModeStatus = await this.powerService.getConservationModeStatusIdeaNoteBook();

			if (conservationModeStatus.available && conservationModeStatus.status !== defaultValue) {
				this.powerService.setConservationModeStatusIdeaNoteBook(defaultValue);
			}
		} catch (error) {
			this.logger.error('PowerBatterySettingsDefaultValueService - setConservationMode', error);
		}
	}

	private async setRapidCharge(defaultValue: boolean) {
		this.logger.info('PowerBatterySettingsDefaultValueService - setRapidCharge - before');
		try {
			const rapidChargeStatus = await this.powerService.getRapidChargeModeStatusIdeaNoteBook();

			if (rapidChargeStatus?.available && rapidChargeStatus.status !== defaultValue) {
				this.powerService.setRapidChargeModeStatusIdeaNoteBook(defaultValue);
			}
		}
		catch (error) {
			this.logger.error('PowerBatterySettingsDefaultValueService - setRapidCharge', error);
		}
	}

	private async setBatteryThresholdDefaultValue() {
		this.logger.info('PowerBatterySettingsDefaultValueService - setBatteryThreshold - getChargeThresholdInfo - before')
		try {
			const thresholdInfo: ChargeThreshold[] = await this.powerService.getChargeThresholdInfo();
			const isAvailable = thresholdInfo.findIndex(info => info.isCapable) !== -1;

			this.logger.info('PowerBatterySettingsDefaultValueService - setBatteryThreshold - getChargeThresholdInfo - subscribe', { isAvailable, thresholdInfo });

			if (!isAvailable) {
				return;
			}

			await this.setBatteryThreshold(thresholdInfo);

		} catch (error) {
			this.logger.error('PowerBatterySettingsDefaultValueService - setBatteryThreshold - getChargeThresholdInfo', error);
		}
	}

	private async setBatteryThreshold(thresholdInfo: any[]): Promise<any> {
		const batteryThreshold = this.defaultValues['battery-threshold'];
		const enabled = batteryThreshold.enabled === ADPolicyState.ENABLED;

		this.powerService.setAutoChargeThreshold(false);
		if (enabled) {
			const start = toNumber(batteryThreshold.start) || this.defaultStartValue;
			const stop = toNumber(batteryThreshold.stop) || this.defaultStopValue;
			const autoStartCharging = batteryThreshold.autoStartCharging === ADPolicyState.ENABLED;
			const startValue = (start >= stop) || autoStartCharging ? stop - 5 : start;

			this.logger.info('PowerBatterySettingsDefaultValueService - setBatteryThreshold - setBatteryThreshold', { start, startValue, stop, autoStartCharging });

			thresholdInfo.forEach(async info => {
				info.isEnabled = true;
				info.checkboxValue = autoStartCharging;
				info.stopValue = this.roundChargeValue(stop);
				info.startValue = this.roundChargeValue(startValue);

				this.logger.info('PowerBatterySettingsDefaultValueService - setBatteryThreshold - info', info);

				await this.powerService.setChargeThresholdValue(info);
			});
		} else {
			this.logger.info('PowerBatterySettingsDefaultValueService - setBatteryThreshold - setToggleOff', thresholdInfo.length);
			this.powerService.setToggleOff(thresholdInfo.length);
		}
	}

	private roundChargeValue(value: number): number {
		const roundedValue = Math.ceil(value / 5) * 5;
		this.logger.info('PowerBatterySettingsDefaultValueService', { value, roundedValue });
		return roundedValue;
	}
}
